import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  UnorderedList,
} from "@chakra-ui/react";
import { eventIsDesignContest } from "../../sourcing-events/buyer/wizard/utils/designContest";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EndedContractWinnersAlert } from "../award/EndedContractWinnersAlert";
import { UnassessedWinnersAlert } from "../award/UnassessedWinnersAlert";
import {
  BidStatus,
  SourcingEventStatus,
  type EvalBidDto,
  type ReceivedBidDto,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import { useAwardSelection } from "../award/AwardSelectionContext";
import { juryOnlyAdvises, rejectedRankedAboveWinners } from "../award/juryRanking";
import { requiresStandstill } from "../award/requiresStandstill";
import { VoluntaryStandstillField } from "../award/VoluntaryStandstillField";
import { WinnerDocumentationStatement } from "../award/WinnerDocumentationStatement";
import { StaleDecisionsBanner } from "../evaluation/staleScore";
import { flaggedAmong } from "../designContest/jury";
import { relativeAdvantagesLabel } from "../award/relativeAdvantagesLabel";
import { documentationNoteTooLong } from "../award/DocumentationNoteField";
import {
  MAX_RELATIVE_ADVANTAGES_LENGTH,
  relativeAdvantagesHelpText,
  relativeAdvantagesTooLong,
} from "../award/relativeAdvantages";

export const AWARD_CONFIRMATION_STANDSTILL_ID = "award-confirmation-standstill";

const VoluntaryStandstillOutcome = () => {
  const { t } = useTranslation();
  const { standstillDays, standstillProblem } = useAwardSelection();
  if (standstillProblem != null) return null;
  const days = Number(standstillDays.trim());
  return (
    <Text mt="3">
      {days === 0
        ? t("This award has no standstill period.")
        : t(
            "This award has a voluntary standstill period of {{count}} days. Next you approve and send the award letters. The standstill period starts when they are sent.",
            { count: days },
          )}
    </Text>
  );
};

const RelativeAdvantagesField = ({
  text,
  onChange,
  isDisabled,
  helpText,
  designContest,
}: {
  text: string;
  onChange: (text: string) => void;
  isDisabled: boolean;
  helpText: string;
  designContest: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <FormControl mt="3" isInvalid={relativeAdvantagesTooLong(text)}>
      <FormLabel fontSize="sm">{relativeAdvantagesLabel(t, designContest)}</FormLabel>
      <Textarea
        value={text}
        onChange={(event) => onChange(event.target.value)}
        isDisabled={isDisabled}
        rows={4}
        autoFocus
      />
      <FormHelperText>{helpText}</FormHelperText>
      <FormHelperText mt="1">
        {t("{{count}} / {{max}} characters", { count: text.trim().length, max: MAX_RELATIVE_ADVANTAGES_LENGTH })}
      </FormHelperText>
      <FormErrorMessage>
        {t("The text can be at most {{max}} characters.", { max: MAX_RELATIVE_ADVANTAGES_LENGTH })}
      </FormErrorMessage>
    </FormControl>
  );
};

const RelativeAdvantagesSummary = ({
  text,
  isDisabled,
  onWrite,
  designContest,
}: {
  text: string;
  isDisabled: boolean;
  onWrite: () => void;
  designContest: boolean;
}) => {
  const { t } = useTranslation();
  if (text.trim() === "") {
    return (
      <Alert status="info" rounded="md" fontSize="sm" mt="3" alignItems="flex-start">
        <AlertIcon />
        <Box>
          <Text>
            {t(
              "No relative advantages are written. The rejection letters and the protocol will have no text from the award to start from.",
            )}
          </Text>
          <Button variant="link" colorScheme="teal" size="sm" mt="1" onClick={onWrite} isDisabled={isDisabled}>
            {t("Write the relative advantages")}
          </Button>
        </Box>
      </Alert>
    );
  }
  return (
    <Box mt="3">
      <Text fontSize="sm" color="smMuted" mb="1">
        {relativeAdvantagesLabel(t, designContest)}
      </Text>
      <Text
        whiteSpace="pre-wrap"
        borderLeft="2px solid"
        borderColor="smBorder"
        pl="3"
        maxH="40"
        overflowY="auto"
      >
        {text.trim()}
      </Text>
    </Box>
  );
};

export const AwardConfirmationModal = ({
  event,
  bids,
  evaluationBids,
  onConfirm,
  onClose,
}: {
  event: SourcingEventDto;
  bids: ReceivedBidDto[];
  evaluationBids: EvalBidDto[];
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const {
    winnerIds,
    noStandstill,
    standstillProblem,
    juryRankingDeparture,
    ceilingProblem,
    documentationCheckRequired,
    documentationCheckOffered,
    documentationChecked,
    documentationNote,
    relativeAdvantages,
    setRelativeAdvantages,
    tooManyWinners,
    endedContractWinners = [],
    unassessedWinners = [],
  } = useAwardSelection();
  const [isConfirming, setIsConfirming] = useState(false);
  const [writesAdvantages, setWritesAdvantages] = useState(false);
  const winners = bids.filter((bid) => winnerIds.includes(bid.id));
  const voluntary = event.choosesVoluntaryStandstill ?? false;
  const withStatutoryStandstill = !voluntary && requiresStandstill(event) && !noStandstill;
  const contest = eventIsDesignContest(event);
  const oneWinner = winners.length === 1;
  const designContest = event.eventContext.type === "Public" ? event.eventContext.designContest : null;
  const flaggedWinners = winners.filter((bid) => flaggedAmong(designContest, winnerIds).includes(bid.id));
  const rejectedAbove = rejectedRankedAboveWinners(
    event,
    winnerIds,
    new Set(bids.filter((bid) => bid.status === BidStatus.Rejected).map((bid) => bid.id)),
  );

  const confirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={() => !isConfirming && onClose()} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {!contest
            ? t("Award to the selected suppliers?")
            : oneWinner
              ? t("Choose the winner of the design contest?")
              : t("Choose the winners of the design contest?")}
        </ModalHeader>
        <ModalCloseButton isDisabled={isConfirming} />
        <ModalBody>
          <Text>
            {!contest
              ? t("Do you want to award the contract to:")
              : juryOnlyAdvises(event)
                ? oneWinner
                  ? t("After the jury's assessment, you choose this entry as the winner:")
                  : t("After the jury's assessment, you choose these entries as winners:")
                : rejectedAbove.length > 0
                  ? oneWinner
                    ? t("The winner is the highest-ranked entry in the jury's ranking that is not rejected:")
                    : t("The winners are the highest-ranked entries in the jury's ranking that are not rejected:")
                  : oneWinner
                    ? t("The jury's decision names this entry as the winner:")
                    : t("The jury's decision names these entries as winners:")}
          </Text>
          <UnorderedList mt="2" mb={withStatutoryStandstill || voluntary ? "3" : undefined}>
            {winners.map((bid) => (
              <ListItem key={bid.id}>{bid.ownerName}</ListItem>
            ))}
          </UnorderedList>
          {rejectedAbove.map(({ bidId, place }) => (
            <Alert key={bidId} status="info" rounded="md" fontSize="sm" mb="2">
              <AlertIcon />
              {t("{{entry}}, which the jury ranked no. {{place}}, is rejected.", {
                entry: bids.find((bid) => bid.id === bidId)?.ownerName,
                place,
              })}
            </Alert>
          ))}
          <EndedContractWinnersAlert suppliers={endedContractWinners} mb="2" />
          <UnassessedWinnersAlert eventId={event.id} winners={unassessedWinners} mb="2" />
          {flaggedWinners.length > 0 && (
            <Alert status="warning" rounded="md" fontSize="sm" mb="2">
              <AlertIcon />
              {t(
                "The jury has flagged {{entries}} as identifying. Choosing it as a winner records in the protocol that you went ahead.",
                { entries: flaggedWinners.map((bid) => bid.ownerName).join(", "), count: flaggedWinners.length },
              )}
            </Alert>
          )}
          {juryRankingDeparture === "REFUSED_BY_BINDING_JURY" && (
            <Alert status="warning" rounded="md" fontSize="sm" mb="2">
              <AlertIcon />
              {t("The jury's decision is binding, so the winners must be the entries the jury ranked highest.")}
            </Alert>
          )}
          {contest && <Text>{t("Next you approve and send the letters to the participants. A design contest has no standstill period.")}</Text>}
          {withStatutoryStandstill && (
            <Text>
              {t("Next you approve and send the award letters. The standstill period starts when they are sent.")}
            </Text>
          )}
          {voluntary && (
            <>
              <VoluntaryStandstillField
                id={AWARD_CONFIRMATION_STANDSTILL_ID}
                utilitiesPartIII={event.standstillUnderUtilitiesPartIII ?? false}
                partI={event.standstillWithoutProvision ?? false}
              />
              <VoluntaryStandstillOutcome />
            </>
          )}
          {documentationCheckOffered && (
            <WinnerDocumentationStatement
              suppliers={winners.map((bid) => bid.ownerName)}
              rule={event.winnerDocumentation?.rule}
            />
          )}
          {writesAdvantages ? (
            <RelativeAdvantagesField
              text={relativeAdvantages ?? ""}
              onChange={setRelativeAdvantages}
              isDisabled={isConfirming}
              helpText={relativeAdvantagesHelpText(t, event, contest)}
              designContest={contest}
            />
          ) : (
            !relativeAdvantagesTooLong(relativeAdvantages) && (
              <RelativeAdvantagesSummary
                text={relativeAdvantages ?? ""}
                isDisabled={isConfirming}
                onWrite={() => setWritesAdvantages(true)}
                designContest={contest}
              />
            )
          )}
          {!writesAdvantages && relativeAdvantagesTooLong(relativeAdvantages) && (
            <Alert status="warning" rounded="md" fontSize="sm" mt="3">
              <AlertIcon />
              {t("The relative advantages can be at most {{max}} characters. Shorten them on the award tab.", {
                max: MAX_RELATIVE_ADVANTAGES_LENGTH,
              })}
            </Alert>
          )}
          <Box mt="3">
            <StaleDecisionsBanner
              bids={evaluationBids}
              evaluationCompleted={event.status === SourcingEventStatus.Evaluated}
            />
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isConfirming}>
            {t("Cancel")}
          </Button>
          <Button
            variant="outline"
            colorScheme="teal"
            onClick={() => void confirm()}
            isLoading={isConfirming}
            isDisabled={
              (voluntary && standstillProblem != null) ||
              juryRankingDeparture === "REFUSED_BY_BINDING_JURY" ||
              ceilingProblem != null ||
              (documentationCheckRequired && !documentationChecked) ||
              (documentationCheckOffered && documentationChecked && documentationNoteTooLong(documentationNote)) ||
              relativeAdvantagesTooLong(relativeAdvantages) ||
              tooManyWinners ||
              endedContractWinners.length > 0 ||
              unassessedWinners.length > 0
            }
          >
            {t("Confirm")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
